import type { ReactNode } from "react";

type SegmentedOption<T extends string> =
  | T
  | { value: T; label: ReactNode; icon?: ReactNode };

/** Pill-grouped segmented control. Matches
 *  design-handoff/project/components.jsx:Segmented.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = "md",
}: {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (next: T) => void;
  size?: "sm" | "md";
}) {
  const pad = size === "sm" ? "5px 10px" : "7px 14px";
  const fz = size === "sm" ? 12 : 13;
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 3,
        background: "var(--paper-2)",
        borderRadius: 8,
        border: "1px solid var(--rule)",
      }}
    >
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        const icon = typeof o === "string" ? undefined : o.icon;
        const on = v === value;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            style={{
              border: 0,
              background: on ? "var(--card)" : "transparent",
              padding: pad,
              borderRadius: 6,
              fontSize: fz,
              fontWeight: 500,
              color: on ? "var(--ink)" : "var(--ink-3)",
              cursor: "pointer",
              boxShadow: on ? "var(--sh-1)" : "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
            }}
          >
            {icon}
            {label}
          </button>
        );
      })}
    </div>
  );
}
